import { z } from 'zod';

/**
 * What a calendar entry must look like before it can be saved.
 *
 * Mirrors the backend's `calendar.validation.ts` — the server is the authority,
 * these rules only save a round trip. Keep the two in step.
 *
 * The one rule deliberately left to the server is whether the dates fall inside
 * the academic year: the session's own start and end are not in the browser, and
 * guessing them here would reject a valid date on an institute whose year runs
 * differently.
 */

/** The kinds this form writes. Holidays are declared through the holiday form. */
export const EVENT_CATEGORIES = ['EXAM', 'EVENT', 'MEETING', 'IMPORTANT', 'ACADEMIC'] as const;

export const EVENT_AUDIENCES = ['EVERYONE', 'STUDENTS', 'TEACHERS', 'PARENTS'] as const;

export const EVENT_CLASS_SCOPES = [
  'ALL_CLASSES',
  'NO_CLASSES',
  'PRE_PRIMARY',
  'PRIMARY',
  'UPPER_PRIMARY',
  'SECONDARY',
  'SENIOR_SECONDARY',
] as const;

/** Entry titles carry digits, dots, brackets and apostrophes, as holiday names do. */
const TITLE_PATTERN = /^[\p{L}\p{N} .,'()&/-]+$/u;

/** The `YYYY-MM-DD` an `<input type="date">` holds, and the API takes. */
const day = z
  .string()
  .trim()
  .min(1, 'Pick a date')
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-08-15');

/** The `HH:mm` an `<input type="time">` holds. Empty means all day. */
const clockTime = z
  .string()
  .trim()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a 24-hour time like 08:30')
  .optional()
  .or(z.literal(''))
  .transform((value) => value || undefined);

export const eventSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(2, 'Give the entry a title')
      .max(150, 'That title is too long')
      .regex(TITLE_PATTERN, "Letters, numbers, spaces and . , ' ( ) & / - only"),
    category: z.enum(EVENT_CATEGORIES, {
      errorMap: () => ({ message: 'Choose what kind of entry this is' }),
    }),
    startDate: day,
    endDate: day,
    startTime: clockTime,
    endTime: clockTime,
    audience: z.enum(EVENT_AUDIENCES, {
      errorMap: () => ({ message: 'Choose who this is for' }),
    }),
    classScope: z.enum(EVENT_CLASS_SCOPES, {
      errorMap: () => ({ message: 'Choose which classes this applies to' }),
    }),
    description: z
      .string()
      .trim()
      .max(500, 'Keep the note under 500 characters')
      .transform((value) => value || undefined),
  })
  .refine((value) => value.endDate >= value.startDate, {
    message: 'The last day cannot be before the first',
    path: ['endDate'],
  })
  // An end time on its own has nothing to be an end of, and would read as an
  // all-day entry that somehow stops at four.
  .refine((value) => !(value.endTime && !value.startTime), {
    message: 'Give a start time as well, or leave both empty for an all-day entry',
    path: ['startTime'],
  })
  // Only on a single-day entry: a residential trip may start at 09:00 on Monday
  // and end at 16:00 on Friday.
  .refine(
    (value) =>
      !(
        value.startTime &&
        value.endTime &&
        value.endDate === value.startDate &&
        value.endTime <= value.startTime
      ),
    { message: 'The end time must be later than the start time', path: ['endTime'] },
  );

/** What the form holds while it is being filled in — every field a string. */
export interface EventFormValues {
  title: string;
  category: (typeof EVENT_CATEGORIES)[number] | '';
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  audience: (typeof EVENT_AUDIENCES)[number] | '';
  classScope: (typeof EVENT_CLASS_SCOPES)[number] | '';
  description: string;
}

/** What the API takes, once parsed. */
export type EventPayload = z.infer<typeof eventSchema>;

/** A new entry opens with empty fields for mandatory fields so the user explicitly selects them. */
export const EMPTY_EVENT_FORM: EventFormValues = {
  title: '',
  category: '',
  startDate: '',
  endDate: '',
  startTime: '',
  endTime: '',
  audience: '',
  classScope: '',
  description: '',
};
